import { Button } from 'ui';
import { Dashboard, DashboardMainHeaderForm } from 'dashboard/components/dashboard';
import FinancialAccountFormFields, {
  FormFields,
} from 'financialAccounts/components/form/FinancialAccountsFormFields';
import { Page } from 'dashboard/components/dashboard/Breadcrumbs';

import { useForm, SubmitHandler, ErrorOption } from 'react-hook-form';
import { ApolloError, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { setFormError } from 'utils/form';
import {
  FinancialAccountCreated,
  FinancialAccountMutationVariables,
  FINANCIAL_ACCOUNT_CREATE_MUTATION,
} from 'financialAccounts/graphql/financialAccountCreateMutation';
import {
  financialAccountsTypename,
  FinancialAccountList,
  FINANCIAL_ACCOUNTS_QUERY,
} from 'financialAccounts/graphql/financialAccountsQuery';
import useToastContext from 'ui/hooks/useToast';
import { useHistory } from 'react-router';
import { ToastProps } from 'ui/contexts/overlay/Toast';
import { useLoading } from 'ui/contexts/overlay/Loading';
import { useEffect } from 'react';
import { has } from 'lodash';

const DASHBOARD_FINANCIAL_ACCOUNTS_ROUTE =
  '/dashboard/billing-cycle/financial-accounts';

const BREADCRUMB_PAGES: Page[] = [
  {
    name: 'Gestão de Contas Financeiras',
    route: DASHBOARD_FINANCIAL_ACCOUNTS_ROUTE,
    current: false,
  },
  {
    name: 'Contas Financeiras',
    route: DASHBOARD_FINANCIAL_ACCOUNTS_ROUTE,
    current: false,
  },
  {
    name: 'Nova Conta Financeira',
    route: null,
    current: true,
  },
];
const TITLE = 'Nova Conta Financeira';
const CREATE_ERROR_TOAST: ToastProps = {
  text: 'Houve um erro ao tentar criar a conta financeira',
  title: 'Algo deu errado!',
  variant: 'danger',
};

const CREATE_SUCCESS_TOAST: ToastProps = {
  title: 'Successfully saved!',
  variant: 'primary',
  text: 'Anyone with a link can now view this file.',
};

export default function CreateFinancialAccountsPage() {
  const { push } = useHistory();
  const { addToast } = useToastContext();
  const { t } = useTranslation();
  const {
    register,
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<FormFields>();
  const { showLoading, closeLoading, LoadingOverlay } = useLoading();

  const [financialAccountCreateMutation, { loading: loadingCreate }] = useMutation<
    FinancialAccountCreated,
    FinancialAccountMutationVariables
  >(FINANCIAL_ACCOUNT_CREATE_MUTATION, {
    onError(error: ApolloError) {
      if (has(error.graphQLErrors[0], 'details')) {
        addToast(CREATE_ERROR_TOAST);
      }

      setFormError(
        error,
        (field: string, error: ErrorOption) => {
          setError(field as keyof FormFields, error);

          setTimeout(() => clearErrors(), 2500);
        },
        t
      );
    },
    onCompleted() {
      addToast(CREATE_SUCCESS_TOAST);
      push(DASHBOARD_FINANCIAL_ACCOUNTS_ROUTE);
    },
  });

  const onSubmit: SubmitHandler<FormFields> = (financialAccountCreateInput) =>
    financialAccountCreateMutation({
      variables: {
        financialAccountCreateInput,
      },
      update(cache, { data }) {
        const existingData = cache.readQuery<FinancialAccountList>({
          query: FINANCIAL_ACCOUNTS_QUERY,
        });
        cache.writeQuery({
          query: FINANCIAL_ACCOUNTS_QUERY,
          data: {
            financialAccounts: {
              __typename: financialAccountsTypename,
              afterCursor: existingData?.financialAccounts.afterCursor || null,
              beforeCursor: existingData?.financialAccounts.beforeCursor || null,
              entries: [
                data?.financialAccountCreate,
                ...(existingData?.financialAccounts.entries || []),
              ],
            },
          },
        });
      },
    });

  const isLoading = loadingCreate;

  useEffect(() => {
    if (isLoading) {
      showLoading();
      return;
    }

    closeLoading();
  }, [isLoading, showLoading, closeLoading]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <LoadingOverlay />
      <Dashboard
        dashboardHeader={<div className="h-10" />}
        dashboardMainHeaderTitle={
          <DashboardMainHeaderForm breadcrumbPages={BREADCRUMB_PAGES} title={TITLE}>
            <Button type="submit" size="sm" disabled={isLoading}>
              Salvar
            </Button>
          </DashboardMainHeaderForm>
        }
      >
        <FinancialAccountFormFields
          errors={errors}
          register={register}
          control={control}
        />
      </Dashboard>
    </form>
  );
}
